import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useTheme } from '@Sage/hoi-poi-ui';
import {
    getDifference,
    getRegexExec,
    getRegexMatch,
    removeMarkdown,
    removeTags,
} from '@Sage/ssm-utils';
import sanitizeHtml from 'sanitize-html';

import { useWeb5Context } from 'lib/Web5Context';
import { getEntityFromString } from 'utils/getEntityFromString';

import {
    CHART_REGEX,
    CHART_REGEX_GLOBAL,
    COPILOT_CHAT_STREAM_IDENTIFIERS,
    COPILOT_STREAM_HTML_SANITIZE,
    DETAIL_REGEX,
    DETAIL_REGEX_GLOBAL,
    TABLE_REGEX,
    TABLE_REGEX_GLOBAL,
} from './constants';
import { createCopilotChart, loadCopilotChartRuntime } from './copilotChartRuntime';
import {
    createChartPlaceholder,
    createInfoBox,
    createTable,
    createTablePlaceholder,
    replaceFirstTextToken,
} from './dom-utils';
import { processLiveMarkdown } from './utils';
/**
 * Custom React hook for building and processing chat answers with dynamic content.
 * Handles text processing, chart generation, table creation, and interactive elements
 * for a copilot chat interface.
 *
 * @param {Object} params - Hook parameters
 * @param {Object} params.customEventHandlers - Custom event handlers for user interactions
 * @param {Function} [params.customEventHandlers.redirectToDetail] - Handler for detail link clicks
 *
 * @returns {Object} Hook return object
 * @returns {Function} insertTextInDOM - Function to insert text content into DOM with live processing
 * @returns {Function} cleanChatTextForClipboard - Function to clean text for clipboard copying
 * @returns {Function} parseCopilotEmbeds - Function to parse copilot embeds
 *
 * @example
 * ```jsx
 * const { insertTextInDOM, cleanChatTextForClipboard, parseCopilotEmbeds } = useBuildAnswer({
 *   customEventHandlers: {
 *     redirectToDetail: (target) => {
 *       // Handle detail link clicks
 *       const detailId = target.getAttribute('data-fm_detail');
 *       navigate(`/detail/${detailId}`);
 *     }
 *   }
 * });
 * ```
 */
const useBuildAnswer = ({ customEventHandlers } = {}) => {
    const { colors } = useTheme();
    const { t } = useWeb5Context();
    const chartImagesRef = useRef({});
    const isFirstRender = useRef(true);

    // Clear chart images on mount
    useEffect(() => {
        if (isFirstRender.current) {
            chartImagesRef.current = {};
            isFirstRender.current = false;
        }
    }, []);

    // Memoized replacement functions
    const replacementFunctions = useMemo(
        () => ({
            /**
             * Turns a detail identifier into a text node or safe link/bold element.
             * @param {boolean} avoidLink - Whether to avoid creating links and use bold text instead
             * @returns {Function} Appends a DOM node to the given parent
             */
            replaceForDetail:
                (avoidLink = false) =>
                (parent, text) => {
                    const info = getRegexMatch(text, DETAIL_REGEX);
                    const [entityName, id, name] = info.split(',');

                    if (!entityName || !id || !name) {
                        parent.appendChild(document.createTextNode(text));
                        return;
                    }

                    const node = document.createElement(avoidLink ? 'b' : 'a');
                    node.textContent = name;
                    if (!avoidLink) {
                        node.setAttribute('data-fm_detail', `${entityName}-${id}`);
                    }
                    parent.appendChild(node);
                },
        }),
        [],
    );

    /**
     * Creates an info box container for charts and tables
     * @param {Object} params - Info box parameters
     * @param {HTMLElement} params.container - Parent container
     * @param {boolean} params.isChart - Whether this is for a chart
     * @param {boolean} params.isTable - Whether this is for a table
     * @param {string} params.id - Unique identifier
     * @returns {HTMLElement} Created info box body element
     */
    const buildInfoBox = useCallback(
        ({ container, isChart, isTable, id }) => {
            return createInfoBox({ container, isChart, isTable, id, t });
        },
        [t],
    );

    const buildChart = useCallback(
        (id, textBox, match, data) => {
            if (!data || !Object.keys(data).length) return;

            loadCopilotChartRuntime()
                .then(({ Chart, ChartDataLabels }) => {
                    if (!textBox.isConnected || !textBox.textContent.includes(match)) return;

                    const container = replaceFirstTextToken(textBox, match, () =>
                        createChartPlaceholder(id),
                    );
                    if (!container?.isConnected) return;

                    const chartBody = buildInfoBox({ container, isChart: true, id });
                    createCopilotChart({
                        Chart,
                        ChartDataLabels,
                        colors,
                        data,
                        id,
                        onImage: (chartId, image) => {
                            chartImagesRef.current[chartId] = image;
                        },
                        parent: chartBody,
                    });
                })
                .catch((error) => {
                    globalThis.dispatchEvent(
                        new CustomEvent('copilot-chart-load-error', { detail: error }),
                    );
                });
        },
        [buildInfoBox, colors],
    );

    /**
     * Creates a table element from table data
     * @param {Array} tableData - Array of table row objects
     * @returns {HTMLElement} Created table element
     */
    const createTableElement = useCallback(
        (tableData) => {
            return createTable(tableData, replacementFunctions.replaceForDetail());
        },
        [replacementFunctions],
    );

    /**
     * Processes live content to create detail links dynamically
     * @param {HTMLElement} textBox - Text container element
     */
    const processLiveRedirect = useCallback((textBox) => {
        if (!textBox) return;

        const { match, info } = getRegexExec(textBox.textContent || '', DETAIL_REGEX);

        if (!match || !info) return;

        const infoArray = info.split(',');
        if (!infoArray.length) return;

        const [entity, id, name] = infoArray;
        if (!entity || !name || !id) return;

        replaceFirstTextToken(textBox, match, () => {
            const link = document.createElement('a');
            link.textContent = name;
            link.setAttribute('data-fm_detail', `${entity}-${id}`);
            link.setAttribute('id', id);
            return link;
        });
    }, []);

    /**
     * Processes live content to create charts dynamically
     * @param {HTMLElement} textBox - Text container element
     * @param {Object} data - Chart data object with chart configurations
     */
    const processLiveChart = useCallback(
        (textBox, data) => {
            if (!textBox) return;

            const { match, info } = getRegexExec(textBox.textContent || '', CHART_REGEX);
            const id = info;

            if (!match || !id || !data?.[id]) return;

            const reportData = data[id]?.content;
            if (reportData && Object.keys(reportData)?.length) {
                buildChart(id, textBox, match, reportData);
            }
        },
        [buildChart],
    );

    /**
     * Processes live content to create tables dynamically
     * @param {HTMLElement} textBox - Text container element
     * @param {Object} data - Table data object with table configurations
     */
    const processLiveTable = useCallback(
        (textBox, data) => {
            if (!textBox) return;

            const { match, info } = getRegexExec(textBox.textContent || '', TABLE_REGEX);
            const id = info;

            if (!match || !id || !data?.[id]) return;

            const tableData = data[id]?.content;
            const container = replaceFirstTextToken(textBox, match, () =>
                createTablePlaceholder(id),
            );

            if (container && tableData?.length) {
                const tableBody = buildInfoBox({ container, isTable: true, id });
                const table = createTableElement(tableData);
                if (table) tableBody.appendChild(table);
            }
        },
        [buildInfoBox, createTableElement],
    );

    /**
     * Main function to insert text content into DOM with live processing
     * @param {Object} params - Insertion parameters
     * @param {string} [params.text] - Text content to insert
     * @param {string} [params.progress] - Progress text to display
     * @param {HTMLElement} params.chatBox - Chat container element
     * @param {string} [params.prevTextRef] - Previous text reference for diff calculation
     * @param {Object} [params.reports] - Chart data object
     * @param {Object} [params.tables] - Table data object
     */
    const insertTextInDOM = useCallback(
        ({ text, progress, chatBox, prevTextRef, reports, tables }) => {
            if (!chatBox || (!text && !progress)) return;

            const textBox = chatBox.querySelector('.dana-chat-comment-text__inner');
            const prevText = prevTextRef || '';

            if (progress) {
                textBox.classList.toggle('dana-chat-comment-text__progress', true);
                textBox.innerHTML = sanitizeHtml(progress, COPILOT_STREAM_HTML_SANITIZE);
            } else if (text) {
                textBox.classList.toggle('dana-chat-comment-text__progress', false);

                if (!prevText) textBox.innerHTML = '';

                let textPortion = getDifference(prevText, text);
                textPortion = processLiveMarkdown(textPortion);
                textBox.insertAdjacentHTML(
                    'beforeend',
                    sanitizeHtml(textPortion, COPILOT_STREAM_HTML_SANITIZE),
                );

                processLiveRedirect(textBox);
                processLiveTable(textBox, tables);
                processLiveChart(textBox, reports);
            }
        },
        [processLiveRedirect, processLiveTable, processLiveChart],
    );

    /**
     * Generates a markdown table from data array
     * @param {Array} data - Array of objects representing table rows
     * @returns {string} Formatted markdown table string
     */
    const generateMarkdownTable = useCallback((data) => {
        if (!data?.length) return '';

        const headers = Object.keys(data[0]);
        const columnWidths = headers.reduce((acc, header) => {
            acc[header] = Math.max(
                header.length,
                ...data.map((row) => row[header]?.toString().length || 0),
            );
            return acc;
        }, {});

        let markdownTable = '|';
        headers.forEach((header) => {
            markdownTable += ` ${header.padEnd(columnWidths[header])} |`;
        });
        markdownTable += '\n|';

        headers.forEach((header) => {
            markdownTable += ` ${'-'.repeat(columnWidths[header])} |`;
        });
        markdownTable += '\n';

        data.forEach((row) => {
            markdownTable += '|';
            headers.forEach((header) => {
                const value = row[header];
                let cleanValue;
                if (value && typeof value === 'string') {
                    cleanValue = value.replace(DETAIL_REGEX_GLOBAL, (text) => {
                        const info = getRegexMatch(text, DETAIL_REGEX);
                        const [, , name] = info.split(',');
                        return name || '';
                    });
                } else {
                    cleanValue = value || '';
                }
                markdownTable += ` ${cleanValue.toString().padEnd(columnWidths[header])} |`;
            });
            markdownTable += '\n';
        });

        return `\n${markdownTable}`;
    }, []);

    /**
     * Cleans chat text for clipboard copying by removing HTML tags and converting
     * special elements (charts, tables) to markdown format
     * @param {Object} params - Cleaning parameters
     * @param {string} params.value - Text content to clean
     * @param {Object} [params.tables] - Table data object
     * @param {Object} [params.reports] - Chart data object
     * @returns {string} Cleaned text suitable for clipboard
     */
    const cleanChatTextForClipboard = useCallback(
        ({ value, tables, reports }) => {
            let finalText = value;
            finalText = removeTags(finalText);
            finalText = removeMarkdown(finalText);

            // Replace detail links with plain text
            finalText = finalText.replace(DETAIL_REGEX_GLOBAL, (text) => {
                const info = getRegexMatch(text, DETAIL_REGEX);
                const [, , name] = info.split(',');
                return name ? `"${name}"` : text;
            });

            // Replace tables with markdown
            if (tables) {
                finalText = finalText.replace(TABLE_REGEX_GLOBAL, (text) => {
                    const { info } = getRegexExec(text, TABLE_REGEX);
                    const id = info;

                    if (!id || !tables[id]?.length) return '';

                    return generateMarkdownTable(tables[id]);
                });
            }

            // Remove charts placeholders
            finalText = finalText.replace(CHART_REGEX_GLOBAL, '');

            return finalText;
        },
        [generateMarkdownTable],
    );

    const parseDetailLinks = useCallback((text) => {
        const { match, info } = getRegexExec(text, DETAIL_REGEX);
        if (!match || !info) return text;
        const [entityName, id, name, type] = info.split(',').map((item) => item.trim());

        // Validate required fields to avoid generating malformed links
        if (!entityName || !id || !name) return text;

        const route = getEntityFromString(entityName)?.route;
        if (!route) return text;

        const encodedId = encodeURIComponent(id);
        const encodedType = type ? encodeURIComponent(type) : type;
        // Escape markdown special characters in link text: backslashes first, then brackets
        const escapedName = name.replace(/\\/g, '\\\\').replace(/\[/g, '\\[').replace(/\]/g, '\\]');
        const newText = text.replace(
            match,
            !!encodedType
                ? `[${escapedName}](/#${route}/${encodedId}/${encodedType})`
                : `[${escapedName}](/#${route}/${encodedId})`,
        );
        return newText;
    }, []);

    const parseChartEmbeds = useCallback((text) => {
        const id = getRegexMatch(text, CHART_REGEX);
        return id ? `[](#fm_report '{"id":"${id}"}')` : text;
    }, []);

    const parseTableEmbeds = useCallback((text) => {
        const id = getRegexMatch(text, TABLE_REGEX);
        return id ? `[](#fm_table '{"id":"${id}"}')` : text;
    }, []);

    const parseCopilotEmbeds = useCallback(
        (text) => {
            if (!text) return;

            let newText = text.replace(DETAIL_REGEX_GLOBAL, parseDetailLinks);
            newText = newText.replace(CHART_REGEX_GLOBAL, parseChartEmbeds);
            newText = newText.replace(TABLE_REGEX_GLOBAL, parseTableEmbeds);

            return newText;
        },
        [parseChartEmbeds, parseDetailLinks, parseTableEmbeds],
    );

    return {
        insertTextInDOM,
        cleanChatTextForClipboard,
        parseCopilotEmbeds,
    };
};

export default useBuildAnswer;
export { COPILOT_CHAT_STREAM_IDENTIFIERS };
